import React from 'react';
import { FiBriefcase, FiChevronDown, FiX } from 'react-icons/fi';
import { t, tn, useI18n } from '../../i18n';
import OptionPicker, { type PickerOption } from '../../components/OptionPicker';
import { usePopover } from '../../hooks/usePopover';
import './SiteClientFilter.css';

/** Stands in for "no filter" inside the picker, whose values are strings. */
const ALL_CLIENTS = '__all__';

interface SiteClientFilterProps {
  /** One option per client the board can narrow to. */
  options: PickerOption[];
  /** The client the board is narrowed to, or null for every client. */
  value: string | null;
  /** Shown against "All clients". */
  totalSiteCount: number;
  onChange: (value: string | null) => void;
}

/**
 * The site board's client filter: one select-shaped control that opens a
 * searchable list, so the toolbar stays a single row whether the company has
 * three clients or three hundred.
 */
export default function SiteClientFilter({
  options,
  value,
  totalSiteCount,
  onChange,
}: SiteClientFilterProps) {
  useI18n();
  const { isOpen, toggle, close, rootRef, triggerRef } = usePopover();

  const allClientsOption: PickerOption = {
    value: ALL_CLIENTS,
    label: t('All clients'),
    count: totalSiteCount,
    countLabel: tn('{count} site', '{count} sites', totalSiteCount),
    group: ALL_CLIENTS,
  };
  const pickerOptions = [allClientsOption, ...options];

  const selected = (value !== null && options.find((option) => option.value === value))
    || allClientsOption;
  const isFiltered = selected !== allClientsOption;

  const handleSelect = (next: string) => {
    onChange(next === ALL_CLIENTS ? null : next);
    close();
  };

  const handleClear = () => {
    onChange(null);
    // The clear button unmounts with the filter, so focus needs a new home.
    triggerRef.current?.focus();
  };

  return (
    <div className={`client-filter${isFiltered ? ' client-filter--active' : ''}`}>
      <div className="client-filter__anchor" ref={rootRef}>
        <button
          ref={triggerRef}
          type="button"
          className="client-filter__trigger"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label={t('Filter by client: {name}', { name: selected.label })}
          title={selected.label}
          onClick={toggle}
        >
          <FiBriefcase className="client-filter__icon" aria-hidden="true" />
          <span className="client-filter__label">{t('Client')}</span>
          <span className="client-filter__value">{selected.label}</span>
          {selected.count !== undefined && (
            <span className="client-filter__count">{selected.count}</span>
          )}
          <FiChevronDown className="client-filter__caret" aria-hidden="true" />
        </button>

        {isOpen && (
          <OptionPicker
            className="client-filter__popover"
            label={t('Filter by client')}
            options={pickerOptions}
            value={selected.value}
            onSelect={handleSelect}
            onCancel={() => close()}
            searchPlaceholder={t('Find a client…')}
            emptyMessage={t('No clients match')}
          />
        )}
      </div>

      {isFiltered && (
        <button
          type="button"
          className="client-filter__clear"
          aria-label={t('Clear client filter')}
          title={t('Clear client filter')}
          onClick={handleClear}
        >
          <FiX aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
